import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { api, throwIfError } from "../client";
import { wingNvpKeys } from "../query-keys";
import type { NvpStatus } from "@/components/nvp-status-dot";

/** Poll wing NVP connection status (which wings are mounted/online). Mirrors
 * useNvpStatus but against the wing NVP; codes are W{boat} (e.g. WGBR). */
export function useWingNvpStatus() {
  const query = useQuery({
    queryKey: wingNvpKeys.status,
    queryFn: async () => {
      const result = await api.GET("/api/wing-nvp/status");
      return throwIfError(result, "Failed to fetch wing NVP status");
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const boats = query.data?.boats;

  const getWingNvpStatus = useCallback(
    (boatCode: string | null | undefined): NvpStatus => {
      if (!boatCode || !boats) return "unknown";
      // Takes the bare boat code; the W-prefix addressing lives here so no
      // call site can forget it and silently read "unknown".
      const status = boats[`W${boatCode}` as keyof typeof boats];
      if (status === true) return "online";
      if (status === false) return "offline";
      return "unknown";
    },
    [boats],
  );

  return { ...query, getWingNvpStatus };
}
